import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import DateInput from "../DateInput";
import { Plus, Trash2, Save, CalendarRange, AlertTriangle, Camera, RotateCcw, Loader2, ChevronDown, Clock, Database } from "lucide-react";

/**
 * Seasons management section embedded in Admin > Business Settings.
 *
 * Enforces the V1.0.1 spec on both the client and server:
 *   - Season names are dynamic (free-text, no predefined list)
 *   - Unlimited seasons per set
 *   - Start ≤ End
 *   - No overlapping ranges
 *   - No gaps (season B must start exactly on day after season A ends)
 *
 * The exact same rules run on the backend (`pricing.validate_season_set`),
 * so the client-side check is a UX guard, not a security boundary.
 */
export default function SeasonsSection() {
  const [rows, setRows] = useState([]);
  const [initialRows, setInitialRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ── Snapshot state ──────────────────────────────────────────────────
  const [snapshotting, setSnapshotting] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [loadingSnaps, setLoadingSnaps] = useState(false);
  const [restoring, setRestoring] = useState(null); // snapshot id being restored
  const [confirmSnap, setConfirmSnap] = useState(null); // snapshot awaiting confirmation
  const restoreRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/seasons")
      .then((r) => {
        if (cancelled) return;
        const sorted = [...r.data].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
        setRows(sorted.map((s) => ({ id: s.id, name: s.name, start_date: s.start_date, end_date: s.end_date })));
        setInitialRows(sorted);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Close restore dropdown on outside click
  useEffect(() => {
    if (!showRestore) return;
    const handler = (e) => {
      if (restoreRef.current && !restoreRef.current.contains(e.target)) {
        setShowRestore(false);
        setConfirmSnap(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showRestore]);

  const dirty =
    rows.length !== initialRows.length ||
    rows.some((r, i) => {
      const src = initialRows[i];
      return !src || src.name !== r.name || src.start_date !== r.start_date || src.end_date !== r.end_date;
    });

  const addRow = () => {
    const last = rows[rows.length - 1];
    const nextStart = last?.end_date ? nextDayIso(last.end_date) : "";
    setRows([...rows, { name: "", start_date: nextStart, end_date: "" }]);
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Snapshot handlers ───────────────────────────────────────────────
  const handleTakeSnapshot = async () => {
    setSnapshotting(true);
    try {
      const { data } = await api.post("/admin/rates/snapshot?label=Manual+Snapshot");
      toast.success("Snapshot saved", {
        description: `${data.groups_count} car group rates captured at ${new Date(data.created_at).toLocaleString()}.`,
      });
    } catch (err) {
      toast.error("Snapshot failed", { description: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setSnapshotting(false);
    }
  };

  const handleOpenRestore = async () => {
    if (showRestore) {
      setShowRestore(false);
      setConfirmSnap(null);
      return;
    }
    setShowRestore(true);
    setLoadingSnaps(true);
    try {
      const { data } = await api.get("/admin/rates/snapshots");
      setSnapshots(data);
    } catch (err) {
      toast.error("Failed to load snapshots");
    } finally {
      setLoadingSnaps(false);
    }
  };

  const handleRestore = async (snap) => {
    if (confirmSnap !== snap.id) {
      setConfirmSnap(snap.id);
      return;
    }
    setRestoring(snap.id);
    try {
      const { data } = await api.post(`/admin/rates/restore/${snap.id}`);
      toast.success("Rates restored", {
        description: `${data.restored_groups} car group rates restored from "${data.snapshot_label}".`,
      });
      setShowRestore(false);
      setConfirmSnap(null);
    } catch (err) {
      toast.error("Restore failed", { description: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setRestoring(null);
    }
  };

  const clientValidate = (list) => {
    // Empty is allowed (turns off seasonal pricing).
    if (list.length === 0) return null;
    const clean = list.map((r, i) => {
      const name = (r.name || "").trim();
      if (!name) return `Row ${i + 1}: name is required.`;
      if (!r.start_date || !r.end_date) return `Season "${name}": both start and end dates are required.`;
      if (r.start_date > r.end_date) return `Season "${name}": start date is after end date.`;
      return { name, start_date: r.start_date, end_date: r.end_date };
    });
    const err = clean.find((c) => typeof c === "string");
    if (err) return err;

    const seenNames = new Set();
    for (const c of clean) {
      if (seenNames.has(c.name.toLowerCase())) return `Duplicate season name "${c.name}". Each season must have a unique name.`;
      seenNames.add(c.name.toLowerCase());
    }

    const sorted = [...clean].sort((a, b) => a.start_date.localeCompare(b.start_date));
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const cur = sorted[i];
      if (cur.start_date <= prev.end_date) {
        return `Overlap: season "${cur.name}" starts on ${cur.start_date} but "${prev.name}" ends on ${prev.end_date}. "${cur.name}" must start on ${nextDayIso(prev.end_date)} or later.`;
      }
      // v4.7.3 — gaps between seasons are allowed (agencies close out of
      // season); unassigned days simply fall back to base pricing.
    }
    return null;
  };

  const handleSave = async () => {
    const clientErr = clientValidate(rows);
    if (clientErr) {
      toast.warning("Cannot save seasons", { description: clientErr, duration: 8000 });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        seasons: rows.map((r) => ({
          id: r.id || null,
          name: r.name.trim(), start_date: r.start_date, end_date: r.end_date,
        })),
      };
      const { data } = await api.put("/admin/seasons/bulk", payload);
      const sorted = [...data].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
      setRows(sorted.map((s) => ({ id: s.id, name: s.name, start_date: s.start_date, end_date: s.end_date })));
      setInitialRows(sorted);
      toast.success("Seasons saved", { description: `${sorted.length} season${sorted.length === 1 ? "" : "s"} configured for pricing.` });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      toast.error("Save failed", { description: msg, duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded p-6" data-testid="seasons-section">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="font-display text-xl">Seasons</h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 max-w-lg">
            Free-text season names. Ranges must not overlap; gaps are allowed (days outside every season use base pricing). Rates are configured per Car Group on the Fleet page.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Snapshot action group */}
          <button
            type="button"
            onClick={handleTakeSnapshot}
            disabled={snapshotting}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white rounded disabled:opacity-50"
            data-testid="take-snapshot-btn"
            title="Capture a snapshot of all current car group rates"
          >
            {snapshotting ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
            Take Snapshot
          </button>
          <div className="relative" ref={restoreRef}>
            <button
              type="button"
              onClick={handleOpenRestore}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white rounded"
              data-testid="restore-rates-btn"
              title="Restore rates from a previous snapshot"
            >
              <RotateCcw size={13} />
              Restore Rates
              <ChevronDown size={11} className={`transition-transform ${showRestore ? "rotate-180" : ""}`} />
            </button>
            {showRestore && (
              <div
                className="absolute right-0 top-full mt-1 w-[380px] max-h-[360px] overflow-y-auto bg-white dark:bg-[#1a2332] border border-black/15 dark:border-white/15 rounded-lg shadow-xl z-50"
                data-testid="snapshot-dropdown"
              >
                <div className="px-4 py-3 border-b border-black/8 dark:border-white/10 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Available Snapshots
                </div>
                {loadingSnaps && (
                  <div className="px-4 py-6 text-sm text-neutral-500 flex items-center justify-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Loading snapshots…
                  </div>
                )}
                {!loadingSnaps && snapshots.length === 0 && (
                  <div className="px-4 py-6 text-sm text-neutral-500 dark:text-neutral-400 text-center italic">
                    No snapshots yet. Take your first snapshot to enable rate rollback.
                  </div>
                )}
                {!loadingSnaps && snapshots.map((snap) => (
                  <div
                    key={snap.id}
                    className="px-4 py-3 border-b border-black/5 dark:border-white/5 last:border-b-0 hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
                    data-testid={`snapshot-item-${snap.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{snap.label}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                          <span className="inline-flex items-center gap-1">
                            <Clock size={10} />
                            {new Date(snap.created_at).toLocaleString(undefined, {
                              month: "short", day: "numeric", year: "numeric",
                              hour: "2-digit", minute: "2-digit",
                            })}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Database size={10} />
                            {snap.groups_count} groups
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRestore(snap)}
                        disabled={restoring === snap.id}
                        className={`text-xs px-3 py-1.5 rounded flex-shrink-0 transition-colors ${
                          confirmSnap === snap.id
                            ? "bg-amber-500 text-white hover:bg-amber-600"
                            : "bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20"
                        } disabled:opacity-50`}
                        data-testid={`restore-snap-${snap.id}`}
                      >
                        {restoring === snap.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : confirmSnap === snap.id ? (
                          "Confirm restore"
                        ) : (
                          "Restore"
                        )}
                      </button>
                    </div>
                    {confirmSnap === snap.id && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                        Restore all car group rates to this snapshot? Current rates will be overwritten.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white rounded"
            data-testid="add-season"
          >
            <Plus size={13} /> Add season
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-neutral-500">Loading seasons…</p>}

      <div
        className="mb-4 flex gap-2.5 p-3.5 rounded border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/25 text-amber-900 dark:text-amber-200 text-sm"
        data-testid="seasons-warning"
      >
        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
        <p>
          <strong>Attention:</strong> When creating a new season, remember to define custom rates for
          your vehicle groups if they differ from the default rate.
          <br />
          <strong>Rate Snapshot:</strong> Keep a dated copy of all car group rates before each
          seasonal change so you can roll back in one click.
        </p>
      </div>

      {!loading && rows.length === 0 && (
        <div className="text-sm text-neutral-500 dark:text-neutral-400 italic py-6 border border-dashed border-black/10 dark:border-white/10 rounded flex items-center justify-center gap-2">
          <CalendarRange size={14} /> No seasons configured — Car Groups will use their standard daily rate.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-2" data-testid="seasons-list">
          <div className="grid grid-cols-[1fr_150px_150px_40px] gap-4 text-[10px] tracking-[0.2em] uppercase text-neutral-500 dark:text-neutral-400 px-1">
            <span>Season name</span>
            <span>Start</span>
            <span>End</span>
            <span></span>
          </div>
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_150px_150px_40px] gap-4 items-center" data-testid={`season-row-${idx}`}>
              <input
                value={row.name}
                onChange={(e) => updateRow(idx, { name: e.target.value })}
                placeholder="e.g. Easter Peak"
                className="editorial-input season-field w-full text-sm"
                data-testid={`season-name-${idx}`}
              />
              <div className="season-field [&>div]:block [&>div]:w-full">
                <DateInput
                  value={row.start_date || ""}
                  onChange={(v) => updateRow(idx, { start_date: v })}
                  testId={`season-start-${idx}`}
                />
              </div>
              <div className="season-field [&>div]:block [&>div]:w-full">
                <DateInput
                  value={row.end_date || ""}
                  onChange={(v) => updateRow(idx, { end_date: v })}
                  testId={`season-end-${idx}`}
                />
              </div>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                className="h-10 w-10 flex items-center justify-center text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded"
                aria-label="Remove season"
                data-testid={`season-remove-${idx}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end mt-5">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 bg-black text-white dark:bg-white dark:text-black text-sm px-5 py-2 rounded disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="save-seasons"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save seasons"}
        </button>
      </div>
    </div>
  );
}

function nextDayIso(iso) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
